import React from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

// Constants
import { routes } from 'constants/routes'

// Components
import IndexLayout, { Title, Subtitle } from 'components/IndexLayout'

import Form from 'components/Form'
import PasswordInput from 'components/PasswordInput'

// Hooks
import { useToast } from 'containers/ToastManager'

// Styles
import { StyledButton, StyledLink } from './DisableMFAView.styles'

// Types
import { useDisableMfaMutation } from 'gqlTypes'

type FormValues = typeof INITIAL_VALUES

// Helpers
const INITIAL_VALUES = {
  password: '',
}

const VALIDATION_SCHEMA = yup.object().shape({
  password: yup.string().required('Password can’t be blank'),
})

/**
 * Part of the Disable 2Fa Flow, this component
 * renders a confirmation page to disable 2FA
 */
const DisableMFAView: React.FC = () => {
  const { showToast } = useToast()
  const history = useHistory()

  const [disableMFA, { loading }] = useDisableMfaMutation({
    onCompleted: () => {
      showToast(
        'Email sent to confirm disabling 2FA. Check your inbox',
        'positive'
      )

      history.push(routes.PERSONAL_SETTINGS.create({}))
    },
    onError: ({ message }) => {
      showToast(message, 'negative')
    },
  })

  const handleSubmit = (values: FormValues) => {
    disableMFA({
      variables: {
        password: values.password,
      },
    })
  }

  return (
    <IndexLayout headerLink="sign-up" variant="teal">
      <Title>Re-enter Password</Title>
      <Subtitle>
        Please re-enter your password to turn off your two-factor
        authentication.
      </Subtitle>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <Form.Field
              name="password"
              label="Password"
              errorText={touched.password ? errors.password : undefined}>
              <PasswordInput
                name="password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Field>
            <Form.Footer>
              <StyledButton
                small
                variant="primary-untinted"
                type="submit"
                loading={loading}>
                Continue
              </StyledButton>
            </Form.Footer>
          </Form>
        )}
      </Formik>
      <StyledLink
        variant="secondary"
        to={{
          pathname: routes.PERSONAL_SETTINGS.create({}),
        }}
        isUnderlined>
        Cancel
      </StyledLink>
    </IndexLayout>
  )
}

export default DisableMFAView
